import { DeviceCheckNotes, FieldProse, NumberedSection, obj } from './DossierPrimitives';

/**
 * EU IVDR (Reg 2017/746) Article 79 Post-Market Surveillance Plan renderer.
 * Parity: backend/services/dossier/eu_ivdr_pms_renderer (`pms_sections_html`).
 * Five sections: Data Collection / Assessment / Protocols / CAPA / PMPF.
 */
export function IvdrPmsRenderer({ dossier }: { dossier: Record<string, unknown> }) {
  const dc = obj(dossier, 'data_collection');
  const da = obj(dossier, 'data_assessment');
  const pr = obj(dossier, 'protocols');
  const capa = obj(dossier, 'capa');
  const pmpf = obj(dossier, 'pmpf');

  return (
    <div className="space-y-4">
      <DeviceCheckNotes dossier={dossier} />
      <PmsSection
        num={1}
        title="Data Collection (Article 79)"
        fields={[
          [dc['serious_incidents_and_fscas'], 'Serious incidents and FSCAs'],
          [dc['non_serious_incidents_and_side_effects'], 'Non-serious incidents and side-effects'],
          [dc['trend_data'], 'Trend data'],
          [dc['specialist_literature_databases_registers'], 'Specialist literature, databases, registers'],
          [dc['feedback_and_complaints'], 'Feedback and complaints'],
          [dc['public_info_on_similar_devices'], 'Public information on similar devices'],
        ]}
      />
      <PmsSection
        num={2}
        title="Data Assessment"
        fields={[
          [da['methods_to_assess_collected_data'], 'Methods to assess collected data'],
          [da['indicators_and_thresholds_for_benefit_risk_reassessment'], 'Indicators and thresholds for benefit-risk re-assessment'],
          [da['methods_to_investigate_complaints_and_market_experience'], 'Methods to investigate complaints and market experience'],
        ]}
      />
      <PmsSection
        num={3}
        title="Protocols"
        fields={[
          [pr['trend_reporting_protocol'], 'Trend reporting protocol (Article 83)'],
          [pr['communication_protocols'], 'Communication protocols'],
        ]}
      />
      <PmsSection
        num={4}
        title="Corrective and Preventive Action (CAPA)"
        fields={[
          [capa['procedures_to_identify_and_initiate_capa'], 'Procedures to identify and initiate CAPA'],
          [capa['tools_to_trace_devices_needing_capa'], 'Tools to trace devices needing CAPA'],
        ]}
      />
      <PmsSection
        num={5}
        title="Post-Market Performance Follow-up (PMPF)"
        fields={[
          [pmpf['pmpf_plan_or_justification'], 'PMPF plan or justification (Annex XIII Part B)'],
        ]}
      />
    </div>
  );
}

function PmsSection({
  num,
  title,
  fields,
}: {
  num: number;
  title: string;
  fields: [unknown, string][];
}) {
  const present = fields.filter(([v]) => v != null);
  return (
    <NumberedSection prefix="Section" num={num} title={title}>
      {present.length === 0 ? (
        <p className="text-[13.5px] text-muted italic m-0">No content provided for this section.</p>
      ) : (
        present.map(([v, label], i) => (
          <FieldProse key={i} label={`${label}:`} body={v} fallbackHint={label} />
        ))
      )}
    </NumberedSection>
  );
}
